/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@ember/component';
import { isEmpty } from '@ember/utils';
import { scheduleOnce, bind, next, run } from '@ember/runloop';
import merge from 'lodash.merge';
import cioParserRules from 'cio/helpers/wysiwyg/parser-rules';
import {
  EMAIL_COMPOSER_ROLLBACK,
  INSERT_LIQUID,
  INSERT_LIQUID_WITH_FALLBACK_MODAL_CLOSE,
  INSERT_LIQUID_WITH_FALLBACK_MODAL_OPEN,
  LIQUID_ENABLED_FIELD_FOCUS_IN,
  LIQUID_ENABLED_FIELD_FOCUS_OUT,
} from 'cio/services/event-bus';
import { KEY_K } from 'cio/utils/keyboard';
import { buildWaiter } from '@ember/test-waiters';
import { tagName } from '@ember-decorators/component';
import { tracked } from '@glimmer/tracking';
import { getLiquidToInsert } from 'cio/utils/liquid';

let waiter = buildWaiter('cio-wysihtml5-editor:load-waiter');

/*
  Basic Usage:

  ```handlebars
  {{cio-wysihtml-editor
    content=someText}}
  ```
*/

@tagName('')
export default class CioWysihtml5Editor extends Component {
  @service eventBus;
  @service analytics;
  @service masq;

  content = null;

  editor = null; // this is the wysihtml5 editor instance
  isLinkPopupOpen = false;
  linkDropdownInstance = null;
  linkPopupElement = null;
  insertingLiquidForLink = false;
  triggerElement = null;
  @tracked linkUrl = '';
  @tracked linkText = '';
  showAssetLibraryModal = false;

  formatBlocks = [
    {
      label: 'Heading 1',
      value: 'h1',
      className: 'h1',
    },
    {
      label: 'Heading 2',
      value: 'h2',
      className: 'h2',
    },
    {
      label: 'Normal text',
      value: 'div',
      className: 'font-normal',
    },
  ];

  init() {
    super.init(...arguments);
    // We need to do this in order for wysihtml5 to initialize properly
    // It has to do with when the element is properly inserted into the DOM
    if (!this.get('id')) {
      return this.set('id', 'wysihtml5');
    }
  }

  get readOnlyWithoutMasqAccess() {
    return this.isDisabled && !this.masq.readOnly;
  }

  @action
  resetEditor() {
    this.editor.setValue(this.content);
  }

  @action
  initializeEditor() {
    let token = waiter.beginAsync();

    let content = this.get('content');
    let id = this.get('id');
    let editor = new wysihtml5.Editor(id, {
      stylesheets: [],
      toolbar: `${id}-toolbar`,
      parser: wysihtml5.dom.parse,
      parserRules: merge({}, wysihtml5ParserRules, cioParserRules),
    });

    this.set('editor', editor);

    editor.setValue(content);

    window.wysihtml5EditorInstance = editor;

    editor.on('focus', () => {
      scheduleOnce('afterRender', this, () => {
        // We run this in the afterRender loop so that the link insert/edit
        // popup has a chance to render, if necessary. If it is rendered, do not
        // show publish the liquid enabled event
        if (this.anchorElementBeingUpdated != null) {
          return;
        }
        this.get('eventBus').publish(LIQUID_ENABLED_FIELD_FOCUS_IN, ['wysihtml', null]);
      });
    });

    editor.on('blur', () => {
      this.get('eventBus').publish(LIQUID_ENABLED_FIELD_FOCUS_OUT);
    });

    editor.on('change', bind(this, this.contentChanged));
    editor.on('aftercommand:composer', bind(this, this.contentChanged));

    editor.on('paste', bind(this, this.contentPasted));
    editor.on('change_view', bind(this, this.viewChanged));

    editor.on('load', () => {
      editor.composer.element.addEventListener('keyup', this.contentChanged);
      editor.composer.element.addEventListener('keydown', this.onEditorKeyDown);
      editor.composer.element.addEventListener('click', this.somethingWasClicked);
      editor.composer.editableArea.tabIndex = 1;

      this._copyUrls();
      this._toggleEditorState();

      waiter.endAsync(token);
    });

    let eventBus = this.get('eventBus');

    eventBus.subscribe(INSERT_LIQUID, this, this._insertLiquid);
    eventBus.subscribe(INSERT_LIQUID_WITH_FALLBACK_MODAL_OPEN, this, this.onInsertLiquidWithFallbackModalOpen);
    eventBus.subscribe(INSERT_LIQUID_WITH_FALLBACK_MODAL_CLOSE, this, this.onInsertLiquidWithFallbackModalClose);
    eventBus.subscribe(EMAIL_COMPOSER_ROLLBACK, this, this._onEmailComposerRollback);
  }

  selectedText() {
    if (!window.wysihtml5EditorInstance) {
      return null;
    }

    let {
      composer: { doc },
    } = window.wysihtml5EditorInstance;

    if (!doc) {
      return null;
    }

    return doc.getSelection().toString();
  }

  @action
  onInsertLiquidWithFallbackModalOpen() {
    this.insertingLiquidForLink = this.isLinkPopupOpen;
  }

  @action
  onInsertLiquidWithFallbackModalClose() {
    this.insertingLiquidForLink = false;
  }

  @action
  onEditorKeyDown(ev) {
    if (!ev) {
      return;
    }

    let { keyCode, which, metaKey, ctrlKey } = ev;

    let isMac = /Mac/.test(navigator.platform);
    let hasCtrlKey = isMac ? metaKey : ctrlKey;

    if (hasCtrlKey && (keyCode || which) === KEY_K) {
      ev.preventDefault();

      this.setProperties({
        linkText: this.selectedText(),
      });
      this.linkDropdownInstance?.actions?.open();
    }
  }

  @action
  contentChanged() {
    this.set('content', window.wysihtml5EditorInstance.getValue());
  }

  contentPasted() {
    this._restoreCopiedUrls();

    // Copy the URLs again since the editor will clean up attributes after paste
    next(this, this._copyUrls);
  }

  viewChanged(view) {
    if (view === 'composer') {
      // Copy the URLs since the editor will clean up attributes when the view changes
      next(this, this._copyUrls);
    }

    this.analytics.track('email', 'html_toggled', {
      message: this.template.action?.campaign ?? this.template.newsletter ?? this.template.transactionalMessage,
      template: this.template,
      toggledOn: view !== 'composer',
    });
  }

  linkHasImage = false;

  @action
  somethingWasClicked(event) {
    let editor = window.wysihtml5EditorInstance;

    let targetNode = null;

    if (event.target.nodeName === 'A') {
      targetNode = event.target;
    }

    // An image can also be made into a link. When such an image gets clicked,
    // the click event fires on the image - not the link. Check whether the
    // parent is a link - and if not, check whether the "createLink" wysihtml5
    // can be applied on the selection (which would tell us if the image is a
    // link even if it's nested in more tags).
    if (event.target.nodeName === 'IMG') {
      let isParentALink = event.target.parentElement?.nodeName === 'A';

      if (isParentALink) {
        targetNode = event.target.parentElement;
      } else {
        let linkState = editor.composer.commands.state('createLink');
        if (linkState != null && linkState !== false && linkState.length > 0) {
          targetNode = linkState[0];
        }
      }
    }

    if (targetNode && targetNode.nodeName === 'A') {
      if (targetNode !== this.get('anchorElementBeingUpdated')) {
        this.setProperties({
          anchorElementBeingUpdated: targetNode,
          linkText: targetNode.innerText,
          linkUrl: targetNode.getAttribute('href'),
          linkHasImage: Array.from(targetNode.querySelectorAll('img')).length > 0,
        });
        this.linkDropdownInstance?.actions?.open();
      }
    } else {
      this.linkDropdownInstance?.actions?.close();
      if (this.triggerElement != null) {
        // remove focus from the link insert button
        this.triggerElement.blur();
      }
    }
  }

  willDestroyElement() {
    let eventBus = this.get('eventBus');

    eventBus.unsubscribe(INSERT_LIQUID, this, this._insertLiquid);
    eventBus.unsubscribe(INSERT_LIQUID_WITH_FALLBACK_MODAL_OPEN, this, this.onInsertLiquidWithFallbackModalOpen);
    eventBus.unsubscribe(INSERT_LIQUID_WITH_FALLBACK_MODAL_CLOSE, this, this.onInsertLiquidWithFallbackModalClose);
    eventBus.unsubscribe(EMAIL_COMPOSER_ROLLBACK, this, this._onEmailComposerRollback);
    window.wysihtml5EditorInstance = null;
  }

  @action
  _insertLiquid([fieldName, _fieldIndex], liquid, fallback) {
    if (fieldName === 'wysihtml') {
      window.wysihtml5EditorInstance.composer.commands.exec(
        'insertLiquid',
        liquid,
        fallback,
        this.template.templateEngine,
      );
      window.wysihtml5EditorInstance.currentView.element.focus();
    } else if (fieldName === 'linkText' || fieldName === 'linkUrl') {
      this.insertingLiquidForLink = true;
      let fieldNameToSelector = {
        linkText: '.cio-wysihtml-editor__field--link-text input',
        linkUrl: '.cio-wysihtml-editor__field--link-url input',
      };

      this.linkDropdownInstance?.actions?.open();

      let input = this.linkPopupElement?.querySelector(fieldNameToSelector[fieldName]);

      if (input == null) {
        return;
      }

      let { value, selectionStart, selectionEnd } = input;
      let beginStr = value.substr(0, selectionStart);
      let endStr = value.substr(selectionEnd, value.length);
      let liquidToInsert = getLiquidToInsert(liquid, fallback, this.template.templateEngine);
      let newValue = `${beginStr}${liquidToInsert}${endStr}`;

      input.value = newValue;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  _onEmailComposerRollback() {
    let content = this.get('content');

    window.wysihtml5EditorInstance.setValue(content);
  }

  onLinkPopupToggled() {
    let isLinkPopupOpen = this.get('isLinkPopupOpen');

    if (!isLinkPopupOpen) {
      this.setProperties({
        anchorElementBeingUpdated: null,
        linkText: '',
        linkUrl: '',
        linkHasImage: false,
        linkTextErrors: [],
        linkUrlErrors: [],
      });
    } else {
      let selectionContainsImage =
        window.wysihtml5EditorInstance.composer.selection.findNodesInSelection('IMG').length > 0;
      this.set('linkHasImage', selectionContainsImage);

      if (isEmpty(this.get('linkText')) && !selectionContainsImage) {
        this.set('linkText', this.selectedText());
        scheduleOnce('afterRender', () => this.linkPopupElement?.querySelector('.js-link-text-input input')?.focus());
      } else {
        scheduleOnce('afterRender', () => this.linkPopupElement?.querySelector('.js-link-url-input input')?.focus());
      }
    }
  }

  _toggleEditorState() {
    if (this.readOnlyWithoutMasqAccess) {
      window.wysihtml5EditorInstance.disable();
    } else {
      window.wysihtml5EditorInstance.enable();
    }
  }

  _findEditorLinks() {
    if (!window.wysihtml5EditorInstance) {
      return [];
    }

    let {
      composer: { element },
    } = window.wysihtml5EditorInstance;
    return element.querySelectorAll('a');
  }

  // The editor will strip out `data-href` attributes
  // on save because they are not whitelisted.

  _copyUrls() {
    this._findEditorLinks().forEach((link) => {
      let href = link.getAttribute('href');
      link.setAttribute('data-href', href);
    });
  }

  _restoreCopiedUrls() {
    this._findEditorLinks().forEach((link) => {
      let href = link.getAttribute('href');
      let hrefCopy = link.getAttribute('data-href');

      if (hrefCopy && href !== hrefCopy) {
        link.setAttribute('href', hrefCopy);
      }
    });
  }

  @action
  liquidFieldFocusIn(fieldName) {
    this.get('eventBus').publish(LIQUID_ENABLED_FIELD_FOCUS_IN, fieldName);
  }

  @action
  liquidFieldFocusOut(fieldName) {
    this.get('eventBus').publish(LIQUID_ENABLED_FIELD_FOCUS_OUT, fieldName);
  }

  @action
  insertLink(e) {
    e.preventDefault();

    let editor = window.wysihtml5EditorInstance;
    let linkUrl = this.get('linkUrl');
    let linkText = this.get('linkText');

    if (isEmpty(linkText) && !this.linkHasImage) {
      return this.set('linkTextErrors', ['Please enter the display text']);
    }

    if (this.anchorElementBeingUpdated) {
      if (linkUrl) {
        this.anchorElementBeingUpdated.setAttribute('href', linkUrl);

        if (!this.linkHasImage) {
          this.anchorElementBeingUpdated.innerHTML = linkText;
        }

        // We are updating the content without the wysiwyg's knowledge
        // so we won't get a change event and have to manually ensure
        // everything stays synced.
      } else {
        editor.composer.commands.exec('removeLink');
      }
      this.contentChanged();
    } else if (linkUrl) {
      let createLinkOptions = {
        href: linkUrl,
        target: '_blank',
      };

      if (!this.linkHasImage) {
        createLinkOptions.text = linkText;
        // https://github.com/customerio/issues/issues/5796#issuecomment-837997521
        // the createLink command doesn't replace the text when creating a link.
        // When creating a new link, and you edit the link text in the link popup,
        // the text won't actually get modified in the content.
        // deleteContents will first delete the current selection, and then place
        // the link with the correct updated link text
        if (editor.composer.selection.getRange()) {
          editor.composer.selection.deleteContents();
        }
      }
      editor.composer.commands.exec('createLink', createLinkOptions);
    } else {
      editor.composer.commands.exec('insertText', linkText);
    }

    this.insertingLiquidForLink = false;
    this.linkDropdownInstance?.actions?.close();
    this._copyUrls();

    run(() => window.wysihtml5EditorInstance.focus());
  }

  @action
  onFormatText(size) {
    window.wysihtml5EditorInstance.composer.commands.exec('formatBlock', size.value);
    return false;
  }

  @action
  pickFileFromAssetLibrary(imagePath) {
    window.wysihtml5EditorInstance.currentView.element.focus();
    window.wysihtml5EditorInstance.composer.commands.exec('insertImage', imagePath);
    this.set('showAssetLibraryModal', false);
  }

  @action
  registerLinkDropdown(dropdown, triggerElement) {
    this.setProperties({
      linkDropdownInstance: dropdown,
      triggerElement,
    });
  }

  @action
  setUpLinkPopup(element) {
    this.linkPopupElement = element;
  }

  @action
  openLinkPopup() {
    this.isLinkPopupOpen = true;
    this.onLinkPopupToggled();
  }

  @action
  closeLinkPopup(force = false) {
    // If the user is inserting a link, and uses the insert liquid functionality
    // from the data preview on the left, then we shouldn't close the link popup
    // when they decide to insert liquid or interact with the liquid+fallback
    // modal, unless they're closing the link popup, in which case, we need to
    // force close the modal
    if (this.insertingLiquidForLink && !force) {
      return false;
    } else if (this.insertingLiquidForLink && force) {
      this.insertingLiquidForLink = false;
    }

    // This is triggered by the close button, so we need to call the close action
    // on the dropdown instance manually
    if (force) {
      this.linkDropdownInstance?.actions?.close();
    }

    this.isLinkPopupOpen = false;
    this.onLinkPopupToggled();
  }
}
